import { Anchor, Box, Button, Group, Image, Paper, Stack, Text, Tooltip } from '@mantine/core'
import { IconExternalLink } from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'

import { CollapsibleContentSection } from '@/components/CollapsibleContentSection'
// import { EmptyState } from '@/components/EmptyState'
import { TableContentGrid } from '@/components/TableContentGrid'
import { theme } from '@/configs/theme'
import { useGlobalState } from '@/hooks/useGlobalState'
import { UploadedFilePreview } from '@/screens/ContentModuleFormScreen/UploadedFilePreview'
import { useStepResources } from '@/screens/ContentModuleFormScreen/useStepResources'
import type { GetStepsResponse, ResourceType } from '@/services/api/api.types'

import { useContentModuleScreenData } from './ContentModuleScreen'
import { FAQSectionTable } from './ModuleFAQSection'

type StepSectionProps = {
  step: GetStepsResponse[0]
  index: number
  fileId?: string
}

export function StepSection(props: StepSectionProps) {
  const data = useContentModuleScreenData()

  const { api, configStore } = useGlobalState()
  const { isMediaMode } = data

  const stepContentQuery = useQuery({
    queryKey: ['step', 'content', props.step.stepId, props.fileId],
    queryFn: () => api.getContentForPreview({ step_id: props.step.stepId, file_id: props.fileId }),
    enabled: !!props.step.stepId,
  })

  const contentSourceUrl =
    stepContentQuery.data && 'sourceUrl' in stepContentQuery.data ? stepContentQuery.data.sourceUrl : undefined

  const { uploadedFileResource, sourceFileResource } = useStepResources(props.step.stepId, contentSourceUrl)

  const contentAccessLabel = useMemo(() => {
    if (!sourceFileResource) return null
    const showInApp = sourceFileResource.showInApp ?? false
    const useInRag = props.step.useInRag ?? true
    if (showInApp && useInRag) return 'Full Access'
    if (showInApp && !useInRag) return 'Media Only'
    return 'Information Only'
  }, [sourceFileResource, props.step.useInRag])

  const thumbnailUrl = sourceFileResource?.thumbnailS3Key
    ? `${configStore.serverBaseUrl}/files/${sourceFileResource.thumbnailS3Key}`
    : null

  const rows = useMemo(() => {
    const result = [
      { heading: 'File Title', content: <Text fw={600}>{props.step.name}</Text> },
      {
        heading: 'Source',
        content: uploadedFileResource ? (
          <UploadedFilePreview
            fileName={uploadedFileResource.fileName || 'Uploaded file'}
            type={uploadedFileResource.type as ResourceType}
            resourceKey={uploadedFileResource.resourceKey}
            resourceData={uploadedFileResource.data}
          />
        ) : sourceFileResource && (sourceFileResource.type === 'url' || sourceFileResource.type === 'youtube') ? (
          sourceFileResource.data || sourceFileResource.sourceUrl ? (
            <Anchor href={sourceFileResource.data || sourceFileResource.sourceUrl || ''} target="_blank" size="sm">
              <Group gap={6}>
                {sourceFileResource.data || sourceFileResource.sourceUrl}
                <IconExternalLink size={14} />
              </Group>
            </Anchor>
          ) : (
            <Text c="dimmed">URL unavailable</Text>
          )
        ) : (
          <Text c="dimmed">No Source File</Text>
        ),
      },
      ...(thumbnailUrl
        ? [
            {
              heading: 'Thumbnail',
              content: (
                <Paper withBorder radius="md" p="sm" bg="gray.1" maw={420}>
                  <Stack gap="sm">
                    <Box
                      style={{
                        width: '100%',
                        aspectRatio: '16 / 9',
                        borderRadius: 8,
                        overflow: 'hidden',
                        border: '1px solid var(--mantine-color-gray-3)',
                        backgroundColor: 'var(--mantine-color-gray-2)',
                      }}>
                      <Image
                        src={thumbnailUrl}
                        alt="Thumbnail"
                        w="100%"
                        h="100%"
                        fit="cover"
                        style={{ cursor: 'pointer' }}
                        onClick={() => window.open(thumbnailUrl, '_blank')}
                      />
                    </Box>
                    <Group gap="sm" justify="flex-end">
                      <Tooltip label="Open in new tab">
                        <Button
                          variant="subtle"
                          color="gray"
                          size="compact-sm"
                          rightSection={<IconExternalLink size={16} />}
                          onClick={() => window.open(thumbnailUrl, '_blank')}>
                          Preview
                        </Button>
                      </Tooltip>
                    </Group>
                  </Stack>
                </Paper>
              ),
            },
          ]
        : []),
      // Hidden in media mode to match the editor.
      ...(!isMediaMode && sourceFileResource?.showInAppSupported
        ? [
            {
              heading: 'Content Access',
              content: <Text>{contentAccessLabel}</Text>,
            },
          ]
        : []),
      {
        heading: 'File Description',
        content: sourceFileResource?.description ? (
          <Text style={{ whiteSpace: 'pre-wrap' }}>{sourceFileResource.description}</Text>
        ) : (
          <Text c="dimmed">No Description</Text>
        ),
      },
      {
        heading: 'Extracted Content',
        content: (
          <Box>
            <Paper bg={theme.lighten(theme.colors.primaryColors[1], 0.7)} radius="sm" p="md" pb="xl" shadow="0">
              <Text size="sm" fw={500} lineClamp={6} style={{ whiteSpace: 'pre-wrap' }}>
                {stepContentQuery.data?.content}
              </Text>
            </Paper>
            <Group justify="center">
              <Button
                size="sm"
                style={{ marginTop: 'calc(var(--button-height-sm) / -2)' }}
                onClick={() =>
                  data.modal.current?.openModal({
                    title: 'Previewing Extracted Content',
                    data: stepContentQuery.data?.content,
                    opts: {
                      markdown: true,
                    },
                  })
                }>
                View Full Content
              </Button>
            </Group>
          </Box>
        ),
      },
      ...(isMediaMode
        ? []
        : [
            {
              heading: 'File FAQs',
              content: <FAQSectionTable data={props.step.faqs ?? []} />,
            },
          ]),
    ]
    return result
  }, [
    props.step.name,
    props.step.faqs,
    uploadedFileResource,
    sourceFileResource,
    contentAccessLabel,
    thumbnailUrl,
    stepContentQuery.data,
    data.modal,
    isMediaMode,
  ])

  return (
    <CollapsibleContentSection
      defaultCollapsed
      id={`step-${props.step.stepId}`}
      title={props.step.name ? `File Details: ${props.step.name}` : `File ${props.index + 1} Details`}
      ContentElement={<TableContentGrid headingFlexWidth="264px" rows={rows} />}
    />
  )
}
